import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Users, Plus } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useQuickMatch } from '../hooks/matchmaking/useQuickMatch';
import QuickPlayFilters from '@/components/matchmaking/filters/QuickPlayFilters';
import PlayerResults from '@/components/matchmaking/modals/PlayerResults';
import NoPlayersFound from '@/components/matchmaking/modals/NoPlayersFound';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Player } from '@/types/matchmaking';

import { useGroupActivityStore } from '@/store/groupActivityStore';
import GroupActivityList from '@/components/activities/GroupActivityList';
import NewGroupActivityModal from '@/components/activities/NewGroupActivityModal';
import { Button } from '@/components/ui/button';

// Helpers used by your matchmaking hook—reuse here
import { getUserLocation } from '@/lib/utils/location';
import { calculateLocationDistance } from '@/lib/utils/coordinates';

export default function QuickPlay() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const checkWidth = () => setShowFilters(window.innerWidth >= 640);
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const searchKeyRef = useRef<number | undefined>(undefined);
  const isInitialMount = useRef(true);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!location.state?.fromNav) {
      navigate('/dashboard/quick-play', { replace: true, state: { fromNav: true } });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
    document.body.style.zoom = '1';
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    document.documentElement.style.height = 'auto';
  }, []);

  const {
    potentialPlayers,
    isLoading,
    searchTerm,
    selectedSport,
    selectedGender,
    selectedRating,
    showConnectionsOnly,
    sports,
    setSearchTerm,
    setSelectedSport,
    setSelectedGender,
    setSelectedRating,
    setShowConnectionsOnly,
    resetSearch
  } = useQuickMatch();

  const {
    groupActivities,
    isLoading: isLoadingGroups,
    fetchGroupActivities
  } = useGroupActivityStore();

  // Toggle between players and groups, default via initialView
  const [view, setView] = useState<'players' | 'groups'>(
    location.state?.initialView === 'groups' ? 'groups' : 'players'
  );

  // For Create Group modal
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);

  // User geolocation for distance sorting
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  useEffect(() => {
    getUserLocation()
      .then(loc => setUserLocation(loc))
      .catch(() => {});
  }, []);

  // Fetch group activities once
  useEffect(() => {
    fetchGroupActivities();
  }, [fetchGroupActivities]);

  // Filter out past events and sort by distance
  const sortedUpcomingGroupActivities = useMemo(() => {
    const now = new Date();
    const upcoming = groupActivities.filter(a => {
      const dt = new Date(`${a.groupDate}T${a.groupTime}`);
      return dt >= now;
    });
    if (!userLocation) return upcoming;
    return upcoming.sort((a, b) => {
      const distA = calculateLocationDistance(userLocation, a.venueCity);
      const distB = calculateLocationDistance(userLocation, b.venueCity);
      if (distA === -1) return 1;
      if (distB === -1) return -1;
      return distA - distB;
    });
  }, [groupActivities, userLocation]);

  const handleSearchAgain = useRef(() => {
    resetSearch();
  }).current;

  const handleMatchSelect = useRef(async (player: Player) => {
    try {
      // Add match creation logic here
    } catch (error) {
      console.error(error);
    }
  }).current;

  useEffect(() => {
    if (
      location.state?.searchKey !== searchKeyRef.current ||
      location.state?.preSelectedSport ||
      location.state?.preFilledSearch
    ) {
      searchKeyRef.current = location.state?.searchKey;
      setShowFilters(false);
      resetSearch();
      if (location.state?.preSelectedSport) {
        setSelectedSport(location.state.preSelectedSport);
      }
      if (location.state?.preFilledSearch) {
        setSearchTerm(location.state.preFilledSearch);
        setIsSearching(true);
      }
    }
  }, [
    location.state?.searchKey,
    location.state?.preSelectedSport,
    location.state?.preFilledSearch,
    resetSearch,
    setSelectedSport,
    setSearchTerm
  ]);

  const [visibleCount, setVisibleCount] = useState(10);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          setVisibleCount(prev => Math.min(prev + 10, potentialPlayers.length));
        }
      },
      { rootMargin: '100px' }
    );
    const el = loadMoreRef.current;
    if (el) observer.observe(el);
    return () => {
      if (el) observer.unobserve(el);
    };
  }, [potentialPlayers.length]);

  return (
    <DashboardLayout>
      <div className="space-y-6 min-h-[calc(100vh-9rem)]">

        {/* View Toggle + Create Group button */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex bg-dark-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('players')}
              className={cn(
                'px-4 py-2 text-sm font-medium',
                view === 'players'
                  ? 'bg-gold-400 text-dark-50'
                  : 'bg-dark-300 text-dark-800 hover:bg-dark-200'
              )}
            >
              Players
            </button>
            <button
              onClick={() => setView('groups')}
              className={cn(
                'px-4 py-2 text-sm font-medium',
                view === 'groups'
                  ? 'bg-gold-400 text-dark-50'
                  : 'bg-dark-300 text-dark-800 hover:bg-dark-200'
              )}
            >
              Groups
            </button>
          </div>
          {view === 'groups' && (
            <Button
              onClick={() => setShowNewGroupModal(true)}
              className="bg-gold-400 hover:bg-gold-500 text-dark-50 ml-4"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Group
            </Button>
          )}
        </div>

        {view === 'players' ? (
          <>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative"
            >
              <div className={cn(
                'bg-dark-200 rounded-xl border border-dark-300 overflow-hidden transition-all duration-300 p-3'
              )}>
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 mr-2 bg-gold-400/10 rounded-full flex items-center justify-center">
                    <Users className="h-4 w-4 text-gold-400" />
                  </div>
                  <h1 className="font-bold text-white text-base">Connect & Play</h1>
                </div>
                <div className="max-w-lg mx-auto">
                  <QuickPlayFilters
                    searchTerm={searchTerm}
                    selectedSport={selectedSport}
                    selectedGender={selectedGender}
                    selectedRating={selectedRating}
                    showConnectionsOnly={showConnectionsOnly}
                    onSearchChange={value => {
                      setSearchTerm(value);
                      setIsSearching(!!value);
                    }}
                    onSportChange={setSelectedSport}
                    onGenderChange={setSelectedGender}
                    onRatingChange={setSelectedRating}
                    onConnectionsOnlyChange={setShowConnectionsOnly}
                    sports={sports}
                    showFilters={showFilters}
                    onToggleFilters={() => setShowFilters(f => !f)}
                    onSearchAgain={handleSearchAgain}
                    isSearching={isSearching}
                  />
                </div>
              </div>
            </motion.div>

            {isLoading ? (
              <div className="flex items-center justify-center min-h-[calc(100vh-24rem)] px-4">
                {/* Loading spinner */}
              </div>
            ) : potentialPlayers.length === 0 ? (
              <NoPlayersFound />
            ) : (
              <PlayerResults players={potentialPlayers.slice(0, visibleCount)} />
            )}
            {visibleCount < potentialPlayers.length && (
              <div ref={loadMoreRef} className="h-10" />
            )}
          </>
        ) : (
          <GroupActivityList
            activities={sortedUpcomingGroupActivities}
            isLoading={isLoadingGroups}
          />
        )}

        <NewGroupActivityModal
          isOpen={showNewGroupModal}
          onClose={() => setShowNewGroupModal(false)}
        />
      </div>
    </DashboardLayout>
  );
}
